// Partenaires et sponsors (D-033). Le bureau : contrats, pistes partenaires (apportées par les
// membres ou venues de la page publique, D-096), visibilité, lien privé de chaque partenaire ; en tête,
// Partenaires · Prospection · Garder le lien (D-132 : une seule tuile « Partenaires » dans Club › Financer le club ;
// D-139 : trois vrais segments d'une même page, même en-tête, même ⚙, même retour, `partnersHead`). Ses réglages (l'offre
// aux entreprises, le cadeau de l'apporteur, l'app offerte, ce que le club offre) : Réglages · Partenaires, une page en
// rubriques repliables ouverte par la ⚙ (#/partenaires/reglages[/<rubrique>], `renderPartnerSettings`). Les membres : « Nos partenaires » et leurs offres réservées. L'accueil : une
// bannière du jour. Mesure : des compteurs (vues, clics), jamais de suivi des personnes.
import {
  activeSeason, archivePartner, clubOfficialPartner, clubPartners, clubRequests, decideRequest, invite,
  partnerMemberEmails, partnersAdmin, partnerStats, removePartnerMember, renewPartnerLink, savePartner, trackPartners,
  type Partner, type PartnerCard, type PartnerDraft, type PartnerRequest,
} from '../lib/api';
import { officialOfferAdmin, type OfferAdmin } from '../lib/lien-api';
import { bannerOfTheDay, clicks, KIND_HINT, KIND_LABEL, PLACEMENT_LABEL, sumStats, totalsByKind, views, type PartnerKind, type Placement } from '../domain/partners';
import { euros } from '../domain/licences';
import { FREQ_LABEL } from '../domain/pictos';
import type { OfferFrequency } from '../lib/api';
import { localDate } from '../domain/schedule';
import { seasonOf } from '../domain/season';
import { shortDate } from './format';
import { isMinorAccount, type Ctx } from './context';
import { $, $$, confirmTwice, html, mount, openSheet, toast, type Html, armed } from './dom';
import { emptyGuide } from '../guide/empty';
import { logoFromFile } from './theme';
import { offerAdminBlock, wireOfferAdmin } from './official-partner';
import { offerAdminData, renderLeadsAdmin, renderPacksAdmin, renderProgramAdmin } from './apporteurs';
import { renderPartnerPage } from './partner-page';
import { renderGiftsAdmin, renderLienEntry } from './lien';
import { lienBoard } from '../lib/lien-api';
import { pzTodoCount } from '../lib/partner-prospect';
import { packPrice } from '../domain/apporteurs';
import { settingsPage } from './nav';

const today = () => localDate(new Date());
/** Les fréquences d'une offre de partenaire (D-103) : sa seule offre, utilisable dans Avantages. */
export const OFFER_FREQS: OfferFrequency[] = ['unlimited', 'daily', 'match_day', 'monthly', 'once'];
/** « −10 % sur la révision · Illimité ». */
export const offerLine = (offer: string, freq: OfferFrequency | null | undefined, stock: number | null = null) =>
  `${offer}${freq ? ` · ${FREQ_LABEL[freq]}` : ''}${stock ? ` · ${stock} en tout` : ''}`;
const monogram = (name: string) => name.split(/\s+/).filter(Boolean).slice(0, 3).map(w => w[0]!.toUpperCase()).join('');
export const partnerMark = (p: { name: string; logo_url?: string | null; logo?: string | null }, cls = 'pt-logo'): Html => {
  const src = p.logo_url ?? p.logo ?? null;
  return src ? html`<img class="${cls}" src="${src}" alt="Logo de ${p.name}">` : html`<span class="${cls} pt-mono" aria-hidden="true">${monogram(p.name)}</span>`;
};

export async function renderPartners(root: HTMLElement, ctx: Ctx, partnerId?: string): Promise<void> {
  const club = ctx.club;
  if (!club) { mount(root, html`<div class="empty"><p class="empty-title">Aucun club.</p></div>`); return; }
  // La fiche d'un partenaire (D-125) : une page, avec « Garder le lien » ; la feuille de D-033 s'ouvre de là.
  if (partnerId && ctx.adminOf.some(c => c.id === club.id)) return renderPartnerPage(root, ctx, club.id, partnerId);
  if (ctx.adminOf.some(c => c.id === club.id)) return renderAdmin(root, ctx, club.id, club.name);
  return renderMembers(root, club.id, club.name, isMinorAccount(ctx));
}

// --- Membres : « Nos partenaires » ------------------------------------------------------
// Un compte mineur (D-103) : les partenaires qui soutiennent le club, sans offre ni site (la base ne les lui
// donne pas), et ses visites ne comptent pas pour eux.
async function renderMembers(root: HTMLElement, clubId: string, clubName: string, minor = false) {
  const [list, official] = await Promise.all([clubPartners(clubId).catch(() => [] as PartnerCard[]), clubOfficialPartner(clubId).catch(() => null)]);
  mount(root, html`
    <section class="screen">
      <header class="screen-head"><p class="eyebrow">${clubName}</p><h2>Nos partenaires</h2></header>
      ${list.length ? html`
        <p class="muted">${minor ? 'Ils soutiennent le club. Merci à eux !' : 'Ils soutiennent le club. Certains réservent une offre aux membres : pense à eux.'}</p>
        <div class="pt-grid">${list.map(p => html`
          <article class="card pt-card">
            ${partnerMark(p)}
            <div class="pt-main">
              <strong>${p.name}</strong>
              ${official?.partner_id === p.id ? html`<span class="chip chip-accent po-chip">Partenaire officiel de l’app</span>` : ''}
              ${p.level ? html`<span class="muted small">Partenaire ${p.level}</span>` : ''}
              ${p.description ? html`<p class="small">${p.description}</p>` : ''}
              ${p.member_offer && !minor ? html`<span class="chip chip-ok pt-offer">Offre membres : ${p.member_offer}</span>` : ''}
              ${p.website && !minor ? html`<a class="pt-site" href="${p.website}" target="_blank" rel="noopener" data-click="${p.id}">Voir le site ›</a>` : ''}
            </div>
          </article>`)}</div>`
        : html`<div class="empty"><p class="empty-title">Pas encore de partenaire affiché.</p></div>`}
    </section>`);
  if (minor) return;
  trackPartners(list.filter(p => p.member_offer).map(p => p.id), 'offer');
  for (const a of $$<HTMLAnchorElement>(root, '[data-click]')) a.addEventListener('click', () => trackPartners([a.dataset.click!], 'offer', true));
}

/** Accueil des membres : le partenaire du jour (bannière), avec son offre. */
export async function renderPartnerBanner(el: HTMLElement, ctx: Ctx): Promise<void> {
  if (!ctx.club || isMinorAccount(ctx)) return;   // jamais de bannière pour un mineur (D-036, D-103)
  const list = (await clubPartners(ctx.club.id).catch(() => [] as PartnerCard[])).filter(p => p.banner);
  const p = bannerOfTheDay(list, today());
  if (!p) { mount(el, html``); return; }
  mount(el, html`
    <aside class="card pt-banner" aria-label="Partenaire du club">
      ${partnerMark(p)}
      <span class="pt-main">
        <span class="eyebrow">Partenaire du club</span>
        <strong>${p.name}</strong>
        ${p.member_offer ? html`<span class="small">${p.member_offer}</span>` : p.description ? html`<span class="muted small">${p.description}</span>` : ''}
      </span>
      ${p.website ? html`<a class="btn btn-ghost btn-sm" href="${p.website}" target="_blank" rel="noopener" data-click="${p.id}">Voir</a>` : ''}
    </aside>
    <a class="pt-all" href="#/partenaires">Tous nos partenaires ›</a>`);
  trackPartners([p.id], 'banner');
  $(el, '[data-click]')?.addEventListener('click', () => trackPartners([p.id], 'banner', true));
}

// --- Bureau ----------------------------------------------------------------------------
async function renderAdmin(root: HTMLElement, ctx: Ctx, clubId: string, clubName: string) {
  const season = await activeSeason(clubId).catch(() => null);
  const from = seasonOf(today()).from;   // la visibilité de la saison FFBB, depuis le 1er juillet (D-115)
  const monthStart = `${today().slice(0, 8)}01`;
  const [partners, requests, official] = await Promise.all([
    partnersAdmin(clubId), clubRequests(clubId).catch(() => [] as PartnerRequest[]),
    officialOfferAdmin(clubId).catch(() => null as OfferAdmin | null),
  ]);
  const stats = await partnerStats(partners.map(p => p.id), from).catch(() => []);
  const month = sumStats(stats.filter(s => s.day >= monthStart));
  const totals = totalsByKind(partners);
  const reload = () => renderAdmin(root, ctx, clubId, clubName);

  mount(root, html`
    <section class="screen">
      ${partnersHead(ctx, 'partenaires', '', season?.name)}
      <div id="ap-leads"></div>
      ${offerAdminBlock(official, partners)}
      <section class="card pt-sum">
        <div class="lic-kpis">
          <span><b>${partners.length}</b><small>partenaires</small></span>
          <span><b>${euros(totals.sponsor)}</b><small>sponsoring</small></span>
          <span><b>${euros(totals.mecene)}</b><small>mécénat</small></span>
          <span><b>${euros(totals.institution)}</b><small>subventions</small></span>
        </div>
        <p class="small">Visibilité ce mois-ci : <strong>${month.views}</strong> affichages, <strong>${month.clicks}</strong> clics.</p>
        <p class="hint">Sponsoring : une facture (prestation de visibilité). Mécénat : un don, avec reçu fiscal seulement si le club est d'intérêt général. L'argent ne passe pas par l'app.</p>
        <button type="button" class="btn btn-primary btn-sm" id="b-new" data-guide="pt-new">+ Partenaire</button>
      </section>
      <div id="pt-lien"></div>

      ${requests.length ? html`
        <section class="block">
          <h3>Propositions des partenaires · ${requests.length}</h3>
          <ul class="ce-rows">${requests.map(r => html`
            <li><span><strong>${r.partners?.name ?? 'Un partenaire'}</strong><br>
              <span class="small">${r.kind === 'banner' ? `Bannière d'accueil jusqu'au ${shortDate(r.banner_until!)}` : r.kind === 'match' ? '« Match présenté par » (match à domicile)'
                : `Offre membres : ${offerLine(r.offer ?? '', r.offer_frequency ?? 'unlimited', r.offer_stock)}${partners.find(x => x.id === r.partner_id)?.member_offer ? ' (remplace l’offre en ligne)' : ''}`}</span>
              ${r.message ? html`<br><span class="muted small">« ${r.message} »</span>` : ''}</span>
              <span class="btn-row"><button type="button" class="btn btn-primary btn-sm" data-req="${r.id}" data-dec="accepte">Accepter</button>
                <button type="button" class="btn btn-ghost btn-sm" data-req="${r.id}" data-dec="refuse">Refuser</button></span></li>`)}</ul>
          <p class="hint">Acceptée, une demande s'applique toute seule (bannière, match, offre) et le partenaire est prévenu.</p>
        </section>` : ''}

      ${partners.length ? partnerRows(partners, stats)
        : emptyGuide({ art: 'ti-handshake', guide: 'pt-empty', title: 'Ton premier partenaire', tour: 'partenaires',
            text: 'Sa fiche, son logo, son offre aux licenciés : il apparaît sur la page du club, et il a son propre espace avec ses affichages et ses clics.' })}
    </section>`);

  // Apporteurs de partenaires (D-096) : les pistes en haut ; l'offre aux entreprises est dans les réglages (D-132).
  void renderLeadsAdmin($(root, '#ap-leads')!, clubId);
  void renderLienEntry($(root, '#pt-lien')!, ctx);   // Garder le lien (D-125)

  wireOfferAdmin(root, ctx, clubId, official, partners, () => void reload());   // l'app offerte (D-147)
  $(root, '#b-new')!.addEventListener('click', () => openEditor(ctx, clubId, null, [], reload));
  // Une ligne ouvre la fiche du partenaire (D-125) : « Garder le lien », puis « Modifier la fiche ».
  for (const b of $$<HTMLButtonElement>(root, '[data-edit]')) {
    b.addEventListener('click', () => { location.hash = `#/partenaires/${b.dataset.edit}`; });
  }
  for (const b of $$<HTMLButtonElement>(root, '[data-req]')) {
    b.addEventListener('click', async () => {
      b.disabled = true;
      try { await decideRequest(b.dataset.req!, b.dataset.dec as 'accepte' | 'refuse'); toast(b.dataset.dec === 'accepte' ? 'Demande acceptée : c’est en place' : 'Demande refusée'); await reload(); }
      catch { b.disabled = false; toast('Impossible pour le moment.', 'ko'); }
    });
  }
}

/** Les lignes des partenaires du bureau ; `embed` (l'assistant du module) : sans les affichages ni le lien de son espace. */
function partnerRows(partners: Partner[], stats: Awaited<ReturnType<typeof partnerStats>>, embed = false): Html {
  return html`<ul class="list pt-list">${partners.map((p, i) => {
    const st = sumStats(stats, p.id);
    const banner = p.banner_until && p.banner_until >= today();
    return html`
    <li><button type="button" class="pt-row" data-edit="${p.id}" ${i === 0 ? html`data-guide="pt-row"` : ''}>
      ${partnerMark(p)}
      <span class="card-main">
        <strong>${p.name}</strong>
        <span class="muted small">${[p.level, KIND_LABEL[p.kind], p.amount_cents ? euros(p.amount_cents) : ''].filter(Boolean).join(' · ')}</span>
        ${p.member_offer && !p.restricted ? html`<span class="small pt-offer-line">Offre : ${offerLine(p.member_offer, p.member_offer_frequency)}</span>` : ''}
        <span class="occ-chips">
          ${p.restricted ? html`<span class="chip chip-ko">Jamais affiché (loi Évin)</span>` : html`
            ${p.show_public ? html`<span class="chip chip-ok">Page publique</span>` : ''}
            ${banner ? html`<span class="chip chip-ok">Bannière jusqu'au ${shortDate(p.banner_until!)}</span>` : ''}
            ${p.member_offer ? html`<span class="chip chip-mute">Offre membres</span>` : ''}`}
          ${embed ? '' : html`<span class="chip chip-mute">${views(st.views)} · ${clicks(st.clicks)}</span>`}
        </span>
      </span>
      <span class="occ-go" aria-hidden="true">›</span>
    </button>
    ${embed ? '' : html`<a class="pt-space" href="/p/${p.report_token}" target="_blank" rel="noopener">Voir son espace partenaire ›</a>`}</li>`;
  })}</ul>`;
}

/** Les partenaires actuels dans l'assistant du module (D-158, corrigé le 02/11) : la liste du bureau et « + Partenaire »,
 *  la même fiche ; une ligne ouvre la fiche (sa page, « Garder le lien », viendra avec le module activé). Le bureau seul. */
export async function renderPartnersEmbed(root: HTMLElement, ctx: Ctx): Promise<void> {
  const club = ctx.club;
  if (!club || !ctx.adminOf.some(c => c.id === club.id)) { mount(root, html`<p class="muted">Réservé au bureau du club.</p>`); return; }
  const partners = await partnersAdmin(club.id).catch(() => [] as Partner[]);
  const reload = () => renderPartnersEmbed(root, ctx);
  mount(root, html`<div class="nv-stack">
    ${partners.length ? partnerRows(partners, [], true) : html`<p class="muted small">Aucun partenaire noté pour l’instant.</p>`}
    <span class="btn-row"><button type="button" class="btn btn-primary btn-sm" id="b-new" data-guide="pt-new">+ Partenaire</button></span></div>`);
  $(root, '#b-new')!.addEventListener('click', () => openEditor(ctx, club.id, null, [], reload));
  for (const b of $$<HTMLButtonElement>(root, '[data-edit]')) b.addEventListener('click', () => {
    const p = partners.find(x => x.id === b.dataset.edit);
    if (p) openEditor(ctx, club.id, p, [], reload);
  });
}

/** La feuille de la fiche (D-033) : contrat, contacts, visibilité, comptes, espace, dons (D-124). */
export const openPartnerEditor = (ctx: Ctx, clubId: string, p: Partner, stats: Awaited<ReturnType<typeof partnerStats>>, onClosed: () => void) => openEditor(ctx, clubId, p, stats, onClosed);
function openEditor(ctx: Ctx, clubId: string, p: Partner | null, stats: Awaited<ReturnType<typeof partnerStats>>, onClosed: () => void) {
  let kind: PartnerKind = p?.kind ?? 'sponsor';
  let logo: string | null = p?.logo_url ?? null;
  const link = p ? `${location.origin}/p/${p.report_token}` : '';
  const st = p ? sumStats(stats, p.id) : null;
  openSheet(p ? p.name : 'Nouveau partenaire', html`
    <form class="form" id="f-pt" novalidate>
      <div class="pt-logo-edit">
        <span id="pt-logo-prev">${partnerMark({ name: p?.name ?? '?', logo_url: logo }, 'pt-logo pt-logo-lg')}</span>
        <label class="btn btn-ghost btn-sm">Logo<input type="file" accept="image/png,image/jpeg,image/webp" id="pt-file" hidden></label>
        <button type="button" class="btn btn-ghost btn-sm" id="pt-nologo">Retirer</button>
      </div>
      <label class="field"><span>Nom</span><input name="name" value="${p?.name ?? ''}" maxlength="80" required></label>
      <div class="field"><span>Nature</span>
        <div class="seg seg-wrap" role="radiogroup" data-seg="kind">${(Object.keys(KIND_LABEL) as PartnerKind[]).map(k => html`
          <button type="button" role="radio" data-v="${k}" aria-pressed="${String(k === kind)}">${KIND_LABEL[k]}</button>`)}</div></div>
      <p class="hint" id="kind-hint">${KIND_HINT[kind]}</p>
      <div class="row-2">
        <label class="field"><span>Niveau (facultatif)</span><input name="level" value="${p?.level ?? ''}" maxlength="30" list="pt-levels" placeholder="Or, Argent, Maillot…"></label>
        <label class="field"><span>Apport de la saison (€)</span><input name="amount" type="number" inputmode="decimal" min="0" step="1" value="${p?.amount_cents != null ? p.amount_cents / 100 : ''}"></label>
      </div>
      <datalist id="pt-levels"><option>Or</option><option>Argent</option><option>Bronze</option><option>Maillot</option><option>Partenaire officiel</option></datalist>
      <label class="field"><span>Site (facultatif)</span><input name="website" type="url" value="${p?.website ?? ''}" maxlength="200" placeholder="https://"></label>
      <label class="field"><span>Présentation (facultatif)</span><textarea name="description" rows="2" maxlength="280">${p?.description ?? ''}</textarea></label>
      <label class="field"><span>Offre réservée aux membres (facultatif)</span><input name="offer" value="${p?.member_offer ?? ''}" maxlength="140" placeholder="−10 % sur la révision"></label>
      <label class="field"><span>Combien de fois par membre</span><select name="offer_freq">${OFFER_FREQS.map(f => html`
        <option value="${f}" ${(p?.member_offer_frequency ?? 'unlimited') === f ? 'selected' : ''}>${FREQ_LABEL[f]}</option>`)}</select></label>
      <p class="hint">Une seule offre par partenaire : c’est celle que les membres utilisent dans Avantages (« Utiliser »).</p>
      <label class="field"><span>Partenaire depuis (année)</span><input name="since" type="number" inputmode="numeric" min="1900" max="2100" value="${p?.since_year ?? ''}"></label>
      <fieldset class="lic-plan">
        <legend>Contact (bureau seulement)</legend>
        <label class="field"><span>Nom</span><input name="c_name" value="${p?.contact_name ?? ''}" maxlength="80"></label>
        <label class="field"><span>Madame ou Monsieur (facultatif)</span><select name="c_civ"><option value="">—</option>
          ${(['Madame', 'Monsieur'] as const).map(c => html`<option ${p?.contact_civility === c ? 'selected' : ''}>${c}</option>`)}</select></label>
        <div class="row-2">
          <label class="field"><span>E-mail</span><input name="c_email" type="email" value="${p?.contact_email ?? ''}" maxlength="120"></label>
          <label class="field"><span>Téléphone</span><input name="c_phone" type="tel" value="${p?.contact_phone ?? ''}" maxlength="20"></label>
        </div>
      </fieldset>
      <fieldset class="lic-plan">
        <legend>Visibilité</legend>
        <label class="check"><input type="checkbox" name="show_public" ${p?.show_public ?? true ? 'checked' : ''}><span><strong>Page publique</strong> <span class="muted small">« Ils nous soutiennent »</span></span></label>
        <label class="field"><span>Bannière d'accueil de l'app jusqu'au (facultatif)</span><input name="banner_until" type="date" value="${p?.banner_until ?? ''}"></label>
        <label class="check"><input type="checkbox" name="restricted" ${p?.restricted ? 'checked' : ''}><span><strong>Alcool ou jeux d'argent</strong> <span class="muted small">jamais affiché, ni sur la page ni dans l'app (loi Évin, mineurs)</span></span></label>
      </fieldset>
      ${p ? html`
        <fieldset class="lic-plan">
          <legend>Comptes du partenaire</legend>
          <p class="small">Le partenaire se connecte avec son e-mail : sa visibilité, les appels du club (il est sollicité par e-mail), ses demandes d'emplacement.</p>
          <div id="pt-accounts"></div>
          <div class="inline-form">
            <input id="pt-invite" type="email" inputmode="email" placeholder="e-mail du partenaire" aria-label="E-mail du partenaire">
            <button type="button" class="btn btn-ghost btn-sm" id="pt-invite-b">Inviter</button>
          </div>
        </fieldset>
        <fieldset class="lic-plan">
          <legend>Espace du partenaire</legend>
          <p class="small">Un lien privé, sans compte : sa visibilité, ses matchs présentés, son offre. ${st ? `Cette saison : ${st.views} affichages, ${st.clicks} clics.` : ''}</p>
          ${st && Object.keys(st.by).length ? html`<p class="muted small">${(Object.keys(st.by) as Placement[]).map(k => `${PLACEMENT_LABEL[k]} : ${views(st.by[k]!.views)}`).join(' · ')}</p>` : ''}
          <div class="pub-link"><a href="${link}" target="_blank" rel="noopener">Ouvrir son espace</a>
            <button type="button" class="btn btn-ghost btn-sm" id="pt-copy">Copier le lien</button>
            <button type="button" class="btn btn-ghost btn-sm" id="pt-renew">Nouveau lien</button></div>
        </fieldset>` : ''}
      <p class="form-msg" role="alert"></p>
      <button class="btn btn-primary" type="submit">${p ? 'Enregistrer' : 'Ajouter le partenaire'}</button>
      ${p ? html`<button type="button" class="btn btn-danger-ghost" id="pt-archive">Retirer ce partenaire</button>` : ''}
    </form>`, (sheet, close) => {
    const f = $<HTMLFormElement>(sheet, '#f-pt')!;
    const msg = (t: string) => { $(f, '.form-msg')!.textContent = t; };
    const preview = () => mount($(sheet, '#pt-logo-prev')!, partnerMark({ name: String(new FormData(f).get('name') || p?.name || '?'), logo_url: logo }, 'pt-logo pt-logo-lg'));
    $(sheet, '[data-seg="kind"]')!.querySelectorAll<HTMLButtonElement>('[data-v]').forEach(b => b.addEventListener('click', () => {
      kind = b.dataset.v as PartnerKind;
      $(sheet, '[data-seg="kind"]')!.querySelectorAll('[data-v]').forEach(x => x.setAttribute('aria-pressed', String(x === b)));
      $(sheet, '#kind-hint')!.textContent = KIND_HINT[kind];
    }));
    $<HTMLInputElement>(sheet, '#pt-file')!.addEventListener('change', async (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (!file) return;
      try { logo = await logoFromFile(file); preview(); }
      catch { msg('Logo : PNG, JPEG ou WebP, pas trop lourd.'); }
    });
    $(sheet, '#pt-nologo')!.addEventListener('click', () => { logo = null; preview(); });
    $(sheet, '#pt-copy')?.addEventListener('click', async () => {
      try { await navigator.clipboard.writeText(link); toast('Lien copié : à envoyer au partenaire'); }
      catch { toast('Copie impossible : ouvre son espace et copie l’adresse.', 'ko'); }
    });
    const renew = $<HTMLButtonElement>(sheet, '#pt-renew');
    if (renew && p) confirmTwice(renew, 'Confirmer : l’ancien lien ne marchera plus', async () => {
      try { await renewPartnerLink(p.id); toast('Nouveau lien créé'); close(); }
      catch { msg('Impossible pour le moment.'); }
    });
    const accounts = async () => {
      if (!p) return;
      const list = await partnerMemberEmails(p.id).catch(() => [] as { user_id: string; email: string }[]);
      mount($(sheet, '#pt-accounts')!, list.length ? html`<ul class="ce-rows">${list.map(a => html`
        <li><span>${a.email}</span><button type="button" class="sheet-x" data-unmember="${a.user_id}" aria-label="Retirer ${a.email}">×</button></li>`)}</ul>`
        : html`<p class="muted small">Aucun compte pour l'instant.</p>`);
      for (const b of $$<HTMLButtonElement>(sheet, '[data-unmember]')) {
        b.addEventListener('click', async () => {
          if (!armed(b)) return;
          try { await removePartnerMember(p.id, b.dataset.unmember!); toast('Compte retiré'); await accounts(); }
          catch { toast('Impossible pour le moment.', 'ko'); }
        });
      }
    };
    void accounts();
    $(sheet, '#pt-invite-b')?.addEventListener('click', async () => {
      if (!p) return;
      const email = ($<HTMLInputElement>(sheet, '#pt-invite')!.value || '').trim();
      if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) { msg('E-mail du partenaire invalide.'); return; }
      const r = await invite('partner', p.id, email);
      if (r.error) { msg('Invitation impossible. Réessaie.'); return; }
      toast(r.status === 'invited' ? 'Invitation envoyée par e-mail' : 'Compte existant rattaché');
      $<HTMLInputElement>(sheet, '#pt-invite')!.value = '';
      await accounts();
    });
    const arch = $<HTMLButtonElement>(sheet, '#pt-archive');
    if (arch && p) confirmTwice(arch, `Confirmer : retirer ${p.name}`, async () => {
      try { await archivePartner(p.id); toast('Partenaire retiré (l’historique reste)'); close(); }
      catch { msg('Impossible pour le moment.'); }
    });
    f.addEventListener('submit', async (e) => {
      e.preventDefault();
      msg('');
      const d = new FormData(f);
      const v = (k: string) => String(d.get(k) ?? '').trim();
      if (!v('name')) { msg('Indique le nom du partenaire.'); return; }
      if (v('website') && !/^https?:\/\/\S+$/.test(v('website'))) { msg('Site : une adresse qui commence par https://'); return; }
      if (v('c_email') && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(v('c_email'))) { msg('E-mail du contact invalide.'); return; }
      if (v('c_phone') && !/^[0-9 +().-]{6,20}$/.test(v('c_phone'))) { msg('Téléphone du contact invalide.'); return; }
      const amount = v('amount') ? Math.round(Number(v('amount').replace(',', '.')) * 100) : null;
      if (amount != null && (Number.isNaN(amount) || amount < 0)) { msg('Montant invalide.'); return; }
      const since = v('since') ? Number(v('since')) : null;
      const draft: PartnerDraft = {
        id: p?.id, club_id: clubId, name: v('name'), kind, level: v('level') || null, logo_url: logo,
        website: v('website') || null, description: v('description') || null, member_offer: v('offer') || null,
        member_offer_frequency: (OFFER_FREQS as string[]).includes(v('offer_freq')) ? v('offer_freq') as OfferFrequency : 'unlimited',
        since_year: since && Number.isInteger(since) ? since : null, amount_cents: amount,
        contact_name: v('c_name') || null, contact_email: v('c_email') || null, contact_phone: v('c_phone') || null,
        contact_civility: v('c_civ') === 'Madame' || v('c_civ') === 'Monsieur' ? v('c_civ') as 'Madame' | 'Monsieur' : null,   // « Bonjour Madame Martin » (D-125)
        show_public: d.get('show_public') === 'on', banner_until: v('banner_until') || null, restricted: d.get('restricted') === 'on',
        position: p?.position ?? 0,
      };
      try { await savePartner(draft); toast(p ? 'Partenaire à jour' : 'Partenaire ajouté'); close(); }
      catch { msg('Enregistrement impossible. Réessaie.'); }
    });
  }, onClosed);
}

/** L'en-tête commun des pages du bureau sur les partenaires (D-132, D-139 maquette n3) : « Partenaires », la saison, la ⚙
 *  (posée par src/ui/nav.ts) et trois vrais segments, Partenaires · Prospection · Garder le lien, qui remplacent l'adresse.
 *  Le segment Prospection dit combien d'entreprises sont à contacter (la tuile du Club ouvre toujours Partenaires). */
export function partnersHead(ctx: Ctx, here: 'partenaires' | 'prospection' | 'lien', intro = '', season?: string): Html {
  const club = ctx.club;
  const s = season ?? seasonOf(today()).name;
  if (club) void pzTodoCount(club.id).then(n => {
    const el = document.querySelector<HTMLElement>('[data-pz-count]');
    if (el && n) { el.textContent = String(n); el.hidden = false; }
  }).catch(() => undefined);
  return html`
    <header class="screen-head"><p class="eyebrow">${club?.name ?? ''} · saison ${s}</p><h2>Partenaires</h2></header>
    <nav class="seg" aria-label="Partenaires" data-guide="pt-seg">
      <a href="#/partenaires" ${here === 'partenaires' ? html`aria-current="page"` : ''}>Partenaires</a>
      <a href="#/prospection" ${here === 'prospection' ? html`aria-current="page"` : ''} data-guide="pt-seg-prospection">Prospection<span class="nv-segn" data-pz-count hidden></span></a>
      <a href="#/lien" ${here === 'lien' ? html`aria-current="page"` : ''} data-guide="pt-seg-lien">Garder le lien</a>
    </nav>
    ${intro ? html`<p class="muted">${intro}</p>` : ''}`;
}

/** Réglages · Partenaires (D-139, maquette n1 ; D-132 : le vrai réglage sorti du quotidien), ouverte par la ⚙ de
 *  Partenaires : l'offre aux entreprises, le cadeau de l'apporteur, l'app offerte par un partenaire et ce que le club
 *  offre à ses partenaires (Garder le lien, D-125 ; c'était une page à part, #/lien/offres y mène). Le bureau seul. */
export async function renderPartnerSettings(root: HTMLElement, ctx: Ctx, open?: string): Promise<void> {
  const club = ctx.club;
  if (!club || !ctx.adminOf.some(c => c.id === club.id)) {
    mount(root, html`<section class="screen"><a class="back" href="#/partenaires">‹ Partenaires</a><div class="empty"><p class="empty-title">Réservé au bureau du club.</p></div></section>`);
    return;
  }
  const [offer, board] = await Promise.all([offerAdminData(club.id), lienBoard(club.id).catch(() => null)]);
  const again = () => renderPartnerSettings(root, ctx);
  const gifts = board?.gifts ?? [];
  const active = gifts.filter(g => g.active).length;
  const packs = offer.packs;
  settingsPage(root, {
    key: 'partenaires', open: open ?? null,
    intro: 'Ce qui se règle une fois : ce que voit une entreprise, ce que le club offre. Le quotidien reste dans Partenaires.',
    rubrics: [
      { key: 'offre', icon: 'ti-handshake', title: 'L’offre aux entreprises', guide: 'pt-sec-offer',
        sum: packs.length ? `${packs.length} ${packs.length > 1 ? 'formules' : 'formule'} · ${packs.slice(0, 4).map(p => (p.price_cents ? `${p.name} ${packPrice(p.price_cents)}` : p.name)).join(', ')}` : 'Aucune formule : la carte « Tu connais une entreprise ? » attend',
        body: (el) => renderPacksAdmin(el, club.id, offer, again) },
      { key: 'apporteur', icon: 'ti-gift', title: 'Le cadeau de l’apporteur', guide: 'pt-sec-gift',
        sum: offer.prog?.gift ? `${offer.prog.gift} · jamais d’argent : des missions Ambassade` : 'À choisir · jamais d’argent : des missions Ambassade',
        body: (el) => renderProgramAdmin(el, club.id, offer, 'gift', again) },
      { key: 'app', icon: 'ti-star', title: 'L’app offerte par un partenaire', guide: 'pt-sec-app',
        sum: offer.prog?.app_option ? 'Proposée · une entreprise par saison' : 'Pas proposée · une entreprise par saison',
        body: (el) => renderProgramAdmin(el, club.id, offer, 'app', again) },
      { key: 'offres', icon: 'ti-heart', title: 'Ce que le club offre', guide: 'pt-sec-gifts',
        sum: `${active} ${active > 1 ? 'attentions' : 'attention'} · pour Garder le lien`,
        body: (el) => renderGiftsAdmin(el, ctx, gifts, again) },
    ],
  }, again);
}
